import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconAlertCircle as AlertCircle, IconArmchair as Armchair, IconRefresh as RefreshCw, IconTicket as Ticket } from '@tabler/icons-react';
import { fetchSeatAssignmentsByEvent } from '../api/seatAssignmentApi';
import { fetchVenue } from '../api/venueApi';
import SeatingCanvas, { type SeatLook } from './event-seating/SeatingCanvas';
import { buildLayout, readableTextOn, seatCategoryColor, seatParts, type SeatInfo } from './event-seating/seating';
import { currencySymbol } from '../utils/currency';
import { seatTicketOptions, tiersFromSeatMap, type SeatOption, type SeatOptionExclusion } from '../utils/tierPricing';

type AssignmentLite = { seatUuid: string; ticketId: string; status: string; price?: number };
type TierPrice = { tier: string; price: string | number };

export interface SeatTicketChoice {
    seatId: string;
    label: string;
    tier?: string;
    options: SeatOption[];
    chosenTicketId?: string;
}

interface SeatMapSelectorProps {
    eventId: string;
    venueId: string;
    onSeatSelect: (seatUuid: string, seatNumber: string) => void;
    onSeatDeselect: (seatUuid: string) => void;
    selectedSeats: string[];
    seatMapType?: 'client' | 'editor';
    tickets?: Array<{ _id: string; name: string; price: number | string; description?: string; tierPrices?: TierPrice[]; tierIds?: string[]; status?: boolean; remained?: number; saleStart?: string; saleEnd?: string }>;
    seatAssignments?: AssignmentLite[];
    onSeatAssignmentsLoaded?: (assignments: AssignmentLite[]) => void;
    onUnresolvedChange?: (seatUuids: string[]) => void;
    seatTicketChoice?: Record<string, string>;
    onSeatTicketChoice?: (seatId: string, ticketId: string | null) => void;
    onSeatChoicesChange?: (choices: SeatTicketChoice[]) => void;
    eventData?: { title: string; date: string; doorsOpen: string; showStart: string; venue: string };
    currency?: string;
}

const SELECTED_FILL = '#22C9CD';
const UNAVAILABLE: SeatLook = { fill: '#BCC2CB', stroke: '#9AA1AC', text: '#6B7280', disabled: true };
const RESERVED: SeatLook = { fill: '#8A93A0', stroke: '#6B7280', text: '#FFFFFF', disabled: true };

const priceLabel = (price: number, symbol: string) => (price > 0 ? `${symbol}${price % 1 ? price.toFixed(2) : price}` : 'Free');
const toNumber = (v: number | string | undefined) => {
    const n = typeof v === 'string' ? parseFloat(v) : v ?? 0;
    return Number.isFinite(n) ? (n as number) : 0;
};
const seatLabel = (s: SeatInfo) => `${s.zone} ${s.row}-${s.number}`;

const LoadingState: React.FC = () => (
    <div className="rounded overflow-hidden bg-white dark:bg-nightElevated border border-gray-200 dark:border-nightBorder" aria-busy="true" aria-label="Loading seat map">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-nightBorder space-y-2">
            <div className="h-4 w-40 rounded bg-gray-200 dark:bg-white/10 animate-pulse" />
            <div className="h-3 w-56 rounded bg-gray-100 dark:bg-white/5 animate-pulse" />
        </div>
        <div className="flex gap-2 px-4 py-3">
            {[0, 1, 2].map(i => <div key={i} className="h-8 w-28 rounded-full bg-gray-100 dark:bg-white/5 animate-pulse" />)}
        </div>
        <div className="relative h-[52vh] min-h-[320px] max-h-[520px] bg-[#F4F5F7] dark:bg-[#E2E5E9] flex flex-col items-center justify-center gap-5 overflow-hidden">
            <div className="h-5 w-48 rounded bg-gray-200" />
            <div className="flex flex-col gap-2.5">
                {[14, 16, 18, 18, 16].map((n, r) => (
                    <div key={r} className="flex justify-center gap-2">
                        {Array.from({ length: n }, (_, i) => (
                            <span
                                key={i}
                                className="h-3.5 w-3.5 rounded-full bg-gray-300/80 animate-pulse"
                                style={{ animationDelay: `${(r * 3 + i) * 45}ms` }}
                            />
                        ))}
                    </div>
                ))}
            </div>
            <span className="text-xs text-gray-500">Loading seat map...</span>
        </div>
    </div>
);

const SeatMapSelector: React.FC<SeatMapSelectorProps> = ({
    eventId,
    venueId,
    onSeatSelect,
    onSeatDeselect,
    selectedSeats,
    tickets = [],
    onSeatAssignmentsLoaded,
    onUnresolvedChange,
    seatTicketChoice,
    onSeatTicketChoice,
    onSeatChoicesChange,
    currency,
}) => {
    const sym = currencySymbol(currency);
    const [filter, setFilter] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const noticeTimer = useRef<number | null>(null);

    const venueQuery = useQuery({
        queryKey: ['venue', venueId],
        queryFn: () => fetchVenue(venueId),
        enabled: Boolean(venueId),
    });
    const seatQuery = useQuery({
        queryKey: ['seat-assignments', eventId],
        queryFn: () => fetchSeatAssignmentsByEvent(eventId),
        enabled: Boolean(eventId),
        refetchInterval: 30000,
    });

    const seatMap = venueQuery.data?.data?.seatMap;
    const layout = useMemo(() => buildLayout(seatMap), [seatMap]);
    const seatById = useMemo(() => new Map(layout.seats.map(s => [s.id, s])), [layout]);

    const assignments = useMemo<AssignmentLite[]>(
        () => (seatQuery.data?.data ?? [])
            .filter(a => a.seatUuid)
            .map(a => ({ seatUuid: a.seatUuid, ticketId: a.ticketId ? String(a.ticketId) : '', status: a.status === 'hold' ? 'reserved' : a.status })),
        [seatQuery.data],
    );

    const ticketList = useMemo(
        () => tickets.filter(t => t._id).map(t => ({ id: t._id, name: t.name || 'Ticket', price: toNumber(t.price) })),
        [tickets],
    );
    const ticketById = useMemo(() => new Map(ticketList.map(t => [t.id, t])), [ticketList]);

    const tiers = useMemo(() => tiersFromSeatMap(seatMap), [seatMap]);
    const tierMode = layout.categories.length > 0;
    const [localSeatTicket, setLocalSeatTicket] = useState<Record<string, string>>({});
    const seatTicket = seatTicketChoice ?? localSeatTicket;
    const chooseSeatTicket = useCallback((seatId: string, ticketId: string | null) => {
        if (onSeatTicketChoice) { onSeatTicketChoice(seatId, ticketId); return; }
        setLocalSeatTicket(prev => {
            if (ticketId === null) {
                if (!(seatId in prev)) return prev;
                const { [seatId]: _removed, ...rest } = prev;
                return rest;
            }
            return { ...prev, [seatId]: ticketId };
        });
    }, [onSeatTicketChoice]);

    const optionsByTier = useMemo(() => {
        const map = new Map<string, SeatOption[]>();
        if (!tierMode) return map;
        const now = Date.now();
        tiers.forEach(t => map.set(t.name, seatTicketOptions(tickets as any, tiers, t.name, now).options));
        return map;
    }, [tierMode, tiers, tickets]);
    const notOnSaleReasonByTier = useMemo(() => {
        const map = new Map<string, string>();
        if (!tierMode) return map;
        const now = Date.now();
        const labels: Record<SeatOptionExclusion, string> = {
            inactive: 'tickets are not active',
            not_mapped: 'no ticket is offered for this section',
            not_started: 'sales have not started',
            ended: 'sales have ended',
            sold_out: 'sold out',
            no_price: 'no price set',
        };
        tiers.forEach(t => {
            const { options, excluded } = seatTicketOptions(tickets as any, tiers, t.name, now);
            if (options.length > 0) return;
            const reasons = Array.from(new Set(excluded.filter(e => e.reason !== 'not_mapped').map(e => labels[e.reason])));
            map.set(t.name, reasons.length ? reasons.join(', ') : labels.not_mapped);
        });
        return map;
    }, [tierMode, tiers, tickets]);
    const optionsOf = useCallback((seat: SeatInfo): SeatOption[] => (seat.category ? optionsByTier.get(seat.category) ?? [] : []), [optionsByTier]);

    const reservedSeats = useMemo(() => {
        const set = new Set<string>();
        assignments.forEach(a => {
            if (a.status === 'reserved') set.add(a.seatUuid);
        });
        return set;
    }, [assignments]);

    const resolvedTicketOf = useCallback((seatId: string): string | undefined => {
        const seat = seatById.get(seatId);
        if (!seat) return undefined;
        const options = optionsOf(seat);
        const chosen = seatTicket[seatId];
        if (chosen && options.some(o => o.ticketId === chosen)) return chosen;
        return options.length === 1 ? options[0].ticketId : undefined;
    }, [seatById, optionsOf, seatTicket]);

    const availableTicket = useMemo(() => {
        const map = new Map<string, string>();
        if (tierMode) {
            layout.seats.forEach(seat => {
                if (reservedSeats.has(seat.id)) return;
                const options = optionsOf(seat);
                if (options.length === 0) return;
                map.set(seat.id, resolvedTicketOf(seat.id) ?? options[0].ticketId);
            });
            return map;
        }
        assignments.forEach(a => {
            if (a.status === 'assigned' && a.ticketId && ticketById.has(a.ticketId)) map.set(a.seatUuid, a.ticketId);
        });
        return map;
    }, [assignments, ticketById, tierMode, layout, reservedSeats, optionsOf, resolvedTicketOf]);

    const seatPriceOf = useCallback((seatId: string): number | undefined => {
        const ticketId = availableTicket.get(seatId);
        if (!ticketId) return undefined;
        if (!tierMode) return ticketById.get(ticketId)?.price;
        const seat = seatById.get(seatId);
        return seat ? optionsOf(seat).find(o => o.ticketId === ticketId)?.price : undefined;
    }, [availableTicket, tierMode, ticketById, seatById, optionsOf]);

    const unresolvedSeats = useMemo(
        () => (tierMode ? selectedSeats.filter(id => seatById.has(id) && availableTicket.has(id) && !resolvedTicketOf(id)) : []),
        [tierMode, selectedSeats, seatById, availableTicket, resolvedTicketOf],
    );

    useEffect(() => { onUnresolvedChange?.(unresolvedSeats); }, [unresolvedSeats]); // eslint-disable-line react-hooks/exhaustive-deps

    const seatChoices = useMemo<SeatTicketChoice[]>(() => {
        if (!tierMode) return [];
        return selectedSeats.flatMap(id => {
            const seat = seatById.get(id);
            if (!seat || !availableTicket.has(id)) return [];
            const options = optionsOf(seat);
            if (options.length <= 1) return [];
            return [{ seatId: id, label: seatLabel(seat), tier: seat.category, options, chosenTicketId: resolvedTicketOf(id) }];
        });
    }, [tierMode, selectedSeats, seatById, availableTicket, optionsOf, resolvedTicketOf]);

    useEffect(() => { onSeatChoicesChange?.(seatChoices); }, [seatChoices]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!seatQuery.data) return;
        if (!tierMode) { onSeatAssignmentsLoaded?.(assignments); return; }
        const effective: AssignmentLite[] = [];
        availableTicket.forEach((_placeholder, seatUuid) => {
            const resolved = resolvedTicketOf(seatUuid);
            if (resolved) effective.push({ seatUuid, ticketId: resolved, status: 'assigned', price: seatPriceOf(seatUuid) });
        });
        reservedSeats.forEach(seatUuid => effective.push({ seatUuid, ticketId: '', status: 'reserved' }));
        onSeatAssignmentsLoaded?.(effective);
    }, [assignments, seatQuery.data, onSeatAssignmentsLoaded, tierMode, availableTicket, reservedSeats, seatPriceOf, resolvedTicketOf]);

    const ticketStats = useMemo(() => {
        if (tierMode) return [] as Array<{ id: string; name: string; price: number; label: string | undefined; left: number }>;
        const counts = new Map<string, number>();
        layout.seats.forEach(s => {
            const t = availableTicket.get(s.id);
            if (t) counts.set(t, (counts.get(t) ?? 0) + 1);
        });
        return ticketList.filter(t => counts.has(t.id)).map(t => ({ ...t, label: undefined as string | undefined, left: counts.get(t.id)! })).sort((a, b) => b.price - a.price);
    }, [layout, availableTicket, ticketList, tierMode]);

    const totalAvailable = useMemo(() => layout.seats.filter(s => availableTicket.has(s.id)).length, [layout, availableTicket]);
    const hasAccessible = useMemo(() => layout.seats.some(s => s.accessible), [layout]);
    const selection = useMemo(() => new Set(selectedSeats), [selectedSeats]);

    useEffect(() => {
        if (!seatQuery.data) return;
        selectedSeats.forEach(id => {
            if (seatById.has(id) && !availableTicket.has(id)) onSeatDeselect(id);
        });
    }, [availableTicket, seatQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => {
        if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    }, []);

    const showNotice = (text: string) => {
        setNotice(text);
        if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
        noticeTimer.current = window.setTimeout(() => setNotice(null), 2200);
    };

    const look = useCallback((id: string, selected: boolean): SeatLook => {
        if (selected) return { fill: SELECTED_FILL, stroke: '#080808', strokeWidth: 2.5, text: '#080808', check: 'dark' };
        const ticketId = availableTicket.get(id);
        if (!ticketId) return reservedSeats.has(id) ? RESERVED : UNAVAILABLE;
        const color = seatCategoryColor(layout, seatById.get(id));
        return { fill: color, stroke: 'rgba(0,0,0,0.25)', text: readableTextOn(color), opacity: filter && filter !== ticketId ? 0.22 : 1 };
    }, [availableTicket, reservedSeats, layout, seatById, filter]);

    const categoryLegend = useMemo(() => {
        const counts = new Map<string, number>();
        layout.seats.forEach(s => {
            if (availableTicket.has(s.id)) {
                const key = s.category ?? '';
                counts.set(key, (counts.get(key) ?? 0) + 1);
            }
        });
        return [...counts.entries()]
            .filter(([name]) => name)
            .map(([name, count]) => {
                const prices = (optionsByTier.get(name) ?? []).map(o => o.price);
                const price = prices.length ? Math.min(...prices) : undefined;
                const priceMax = prices.length ? Math.max(...prices) : undefined;
                return { name, count, price, priceMax, color: layout.categories.find(c => c.name === name)?.color ?? seatCategoryColor(layout) };
            });
    }, [layout, availableTicket, optionsByTier]);

    const tooltip = useCallback((seat: SeatInfo) => {
        const baseTicket = ticketById.get(availableTicket.get(seat.id) ?? '');
        const ticket = baseTicket ? { ...baseTicket, price: seatPriceOf(seat.id) ?? baseTicket.price } : undefined;
        const category = seat.category ? <div className="mt-0.5 text-white/70">{seat.category}</div> : null;
        if (selection.has(seat.id)) {
            return <><div className="mt-0.5 text-[#22C9CD]">Selected{ticket ? ` · ${ticket.name} · ${priceLabel(ticket.price, sym)}` : ''}</div>{category}</>;
        }
        return ticket
            ? <><div className="mt-0.5 text-white/85">{ticket.name} · <span className="font-semibold text-white">{priceLabel(ticket.price, sym)}</span></div>{category}</>
            : <div className="mt-0.5 text-white/60">{reservedSeats.has(seat.id) ? 'Reserved' : tierMode ? `Not on sale${notOnSaleReasonByTier.get(seat.category ?? '') ? ` \u00b7 ${notOnSaleReasonByTier.get(seat.category ?? '')}` : ''}` : 'Unavailable'}</div>;
    }, [ticketById, availableTicket, reservedSeats, selection, sym, seatPriceOf, tierMode, notOnSaleReasonByTier]);

    const handleSelectionChange = (next: Set<string>) => {
        next.forEach(id => {
            if (!selection.has(id)) {
                const seat = seatById.get(id);
                if (tierMode && seat && optionsOf(seat).length > 1) showNotice('Choose a ticket type for this seat in the checkout panel.');
                if (seat) onSeatSelect(id, seatLabel(seat));
            }
        });
        selection.forEach(id => {
            if (!next.has(id)) {
                chooseSeatTicket(id, null);
                onSeatDeselect(id);
            }
        });
    };

    if (venueQuery.isLoading || (seatQuery.isLoading && !seatQuery.data)) return <LoadingState />;

    if (venueQuery.isError || seatQuery.isError) {
        return (
            <div className="rounded bg-white dark:bg-nightElevated border border-gray-200 dark:border-nightBorder px-6 py-10 text-center">
                <AlertCircle className="w-8 h-8 mx-auto mb-3 text-error50" />
                <div className="text-sm font-semibold text-[#222] dark:text-nightTextSecondary mb-1">We couldn't load the seat map</div>
                <p className="text-xs text-[#888] dark:text-nightMuted mb-4">Check your connection and try again.</p>
                <button
                    type="button"
                    onClick={() => { venueQuery.refetch(); seatQuery.refetch(); }}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded bg-primary40 text-white text-sm font-semibold dark:bg-nightAccent dark:text-nightBg"
                >
                    <RefreshCw className="w-4 h-4" /> Try again
                </button>
            </div>
        );
    }

    if (!seatMap || !layout.seats.length) {
        return (
            <div className="rounded bg-white dark:bg-nightElevated border border-gray-200 dark:border-nightBorder px-6 py-10 text-center">
                <Armchair className="w-8 h-8 mx-auto mb-3 text-[#B0B0B0]" />
                <div className="text-sm font-semibold text-[#222] dark:text-nightTextSecondary mb-1">Seat map not available</div>
                <p className="text-xs text-[#888] dark:text-nightMuted">This venue doesn't have a seat map yet.</p>
            </div>
        );
    }

    return (
        <div className="rounded overflow-hidden bg-white dark:bg-nightElevated border border-gray-200 dark:border-nightBorder">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-nightBorder">
                <div className="min-w-0">
                    <h3 className="text-base font-semibold text-[#222] dark:text-nightTextSecondary">Choose your seats</h3>
                    <p className="text-xs text-[#666] dark:text-nightMuted">
                        {totalAvailable ? `${totalAvailable} of ${layout.seats.length} seats available` : 'No seats are available right now'}
                    </p>
                </div>
                <span className={`shrink-0 inline-flex items-center h-7 px-3 rounded-full text-sm font-medium ${selectedSeats.length ? 'bg-[#E6F6F4] dark:bg-[#233530] text-primary40 dark:text-nightTextSecondary' : 'bg-gray-100 dark:bg-white/10 text-[#666] dark:text-nightTextSecondary/70'}`}>
                    {selectedSeats.length} selected
                </span>
            </div>

            {ticketStats.length > 0 && (
                <div className="flex gap-2 px-4 py-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="group" aria-label="Filter by ticket">
                    {ticketStats.length > 1 && (
                        <button
                            type="button"
                            onClick={() => setFilter(null)}
                            aria-pressed={!filter}
                            className={`shrink-0 h-9 px-3.5 rounded-full border text-xs font-semibold transition-colors ${!filter ? 'border-[#222] bg-[#222] text-white dark:border-nightAccent dark:bg-nightAccent dark:text-nightBg' : 'border-gray-200 text-[#444] dark:border-nightBorder dark:text-nightTextSecondary'}`}
                        >
                            All prices
                        </button>
                    )}
                    {ticketStats.map(t => {
                        const active = filter === t.id;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                aria-pressed={active}
                                onClick={() => setFilter(active ? null : t.id)}
                                className={`shrink-0 flex items-center gap-2 h-9 pl-2 pr-3.5 rounded-full border text-xs transition-colors ${active ? 'border-[#222] dark:border-nightAccent bg-gray-50 dark:bg-white/5' : 'border-gray-200 dark:border-nightBorder'}`}
                            >
                                <Ticket className="h-4 w-4 text-[#888] dark:text-nightMuted" />
                                <span className="font-semibold text-[#222] dark:text-nightTextSecondary">{t.label ?? priceLabel(t.price, sym)}</span>
                                <span className="text-[#666] dark:text-nightMuted">{t.name}</span>
                                <span className="text-[#999] dark:text-nightMuted/80">· {t.left} left</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="relative h-[52vh] min-h-[320px] max-h-[520px] border-y border-gray-100 dark:border-nightBorder">
                <SeatingCanvas
                    layout={layout}
                    look={look}
                    tooltip={tooltip}
                    selection={selection}
                    onSelectionChange={handleSelectionChange}
                    mode="pick"
                    onBlockedSeat={seat => {
                        const p = seatParts(seat);
                        showNotice(`${[p.row, p.seat].filter(Boolean).join(', ')} ${reservedSeats.has(seat.id) ? 'is reserved' : "isn't available"}`);
                    }}
                />
                {seatQuery.isFetching && !seatQuery.isLoading && (
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-white/90 dark:bg-nightElevated/90 text-[11px] text-[#666] dark:text-nightMuted shadow-sm">
                        <RefreshCw className="w-3 h-3 animate-spin" /> Updating
                    </span>
                )}
                {notice && (
                    <div role="status" className="absolute left-1/2 top-3 -translate-x-1/2 z-10 max-w-[90%] rounded-full bg-gray-900/90 px-3.5 py-1.5 text-xs text-white shadow-lg">
                        {notice}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-[11px] text-[#666] dark:text-nightMuted">
                {categoryLegend.map(c => (
                    <span key={c.name} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-black/20" style={{ background: c.color }} /> {c.price !== undefined ? `${c.name} · ${c.priceMax !== undefined && c.priceMax !== c.price ? `${priceLabel(c.price, sym)} - ${priceLabel(c.priceMax, sym)}` : priceLabel(c.price, sym)}` : c.name}</span>
                ))}
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-[#080808]" style={{ background: SELECTED_FILL }} /> Your seats</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#9CA3AF] border border-[#6B7280]" /> Reserved</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#E5E7EB] border border-[#D1D5DB]" /> Unavailable</span>
                {hasAccessible && <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#2563EB]" /> Wheelchair accessible</span>}
                <span className="hidden [@media(pointer:coarse)]:inline">Pinch to zoom, drag to move</span>
                <span className="hidden [@media(pointer:fine)]:inline">Scroll to move, Ctrl + scroll to zoom</span>
            </div>
        </div>
    );
};

export default SeatMapSelector;
